import PropTypes from "prop-types";
import { memo, useLayoutEffect, useRef } from "react";
// @mui
import Checkbox from "@mui/material/Checkbox";
// components
import Iconify from "src/components/iconify";
//
import { REMOVED_ROW_CLASS, TREE_INDENT_WIDTH } from "../constants";
import { cellClassName, cellStyle, prepareRow, renderDataCellContent } from "./grid-rows";

function TreeCell({ row, expanded, selected, selectable, content, ctx }) {
  return (
    <div
      className="cg-tree-cell"
      style={row.level ? { paddingLeft: row.level * TREE_INDENT_WIDTH } : undefined}
    >
      <span
        className={`cg-tree-toggle${row.hasChildren ? " cg-tree-toggle-active" : ""}`}
        aria-hidden="true"
      >
        {row.hasChildren ? (
          <Iconify
            icon={expanded ? "eva:arrow-ios-downward-fill" : "eva:arrow-ios-forward-fill"}
            width={18}
          />
        ) : null}
      </span>
      {ctx.selectionMode === "multiple" ? (
        <Checkbox
          size="small"
          className="cg-tree-select"
          checked={selected}
          disabled={!selectable}
          onChange={event => ctx.toggleRowSelection(row, event.target.checked)}
          inputProps={{ "aria-label": ctx.t("selectRow") }}
          sx={{ p: 0.5, mr: 0.5 }}
        />
      ) : null}
      <div className="cg-tree-value">
        {typeof content === "string" || typeof content === "number" ? (
          <span className="cg-tree-text">{content}</span>
        ) : (
          content
        )}
      </div>
    </div>
  );
}

TreeCell.propTypes = {
  row: PropTypes.object,
  expanded: PropTypes.bool,
  selected: PropTypes.bool,
  selectable: PropTypes.bool,
  content: PropTypes.node,
  ctx: PropTypes.object,
};

export const TreeDataRow = memo(function TreeDataRow({
  row,
  rowIndex,
  viewColumns,
  ctx,
  selected,
  selectable,
  expanded,
  alternate,
  version,
  dragging,
  dropInside,
}) {
  const ref = useRef(null);
  const rowInfo = {
    data: row.data,
    key: row.key,
    node: row,
    level: row.level,
    rowIndex,
    rowType: "data",
    isSelected: selected,
    isExpanded: expanded,
    isEditing: false,
  };
  const classes = ["cg-data-row", "cg-tree-row"];

  if (alternate) classes.push("cg-alt");
  if (selected) classes.push("cg-selected");
  if (ctx.clickable) classes.push("cg-clickable");
  if (row.data?.isActive === false) classes.push(REMOVED_ROW_CLASS);
  if (dragging) classes.push("cg-row-dragging");
  if (dropInside) classes.push("cg-drop-inside");

  const baseClassName = classes.join(" ");

  useLayoutEffect(() => {
    prepareRow(ref.current, baseClassName, ctx, {
      rowType: "data",
      data: row.data,
      key: row.key,
      node: row,
      level: row.level,
      rowIndex,
      isSelected: selected,
      isExpanded: expanded,
      values: [],
    });
  }, [baseClassName, ctx, expanded, row, rowIndex, selected, version]);

  return (
    <tr
      ref={ref}
      className={baseClassName}
      data-row-index={rowIndex}
      aria-level={row.level + 1}
      aria-expanded={row.hasChildren ? expanded : undefined}
      aria-selected={ctx.selectionMode !== "none" ? selected : undefined}
    >
      {viewColumns.map(viewColumn => {
        let content =
          viewColumn.kind === "data"
            ? renderDataCellContent(viewColumn, row, rowInfo, rowIndex, ctx)
            : null;

        if (viewColumn.treeColumn)
          content = (
            <TreeCell
              row={row}
              expanded={expanded}
              selected={selected}
              selectable={selectable}
              content={content}
              ctx={ctx}
            />
          );

        return (
          <td
            key={viewColumn.key}
            className={cellClassName(
              viewColumn,
              viewColumn.treeColumn ? "cg-tree-column" : undefined
            )}
            style={cellStyle(viewColumn)}
            data-col-index={viewColumn.index}
            data-align={viewColumn.column?.alignment}
          >
            {content}
          </td>
        );
      })}
    </tr>
  );
});

TreeDataRow.propTypes = {
  row: PropTypes.object,
  rowIndex: PropTypes.number,
  viewColumns: PropTypes.array,
  ctx: PropTypes.object,
  selected: PropTypes.bool,
  selectable: PropTypes.bool,
  expanded: PropTypes.bool,
  alternate: PropTypes.bool,
  version: PropTypes.number,
  dragging: PropTypes.bool,
  dropInside: PropTypes.bool,
};
